import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    useGetAdminNotificationsQuery,
    useMarkAdminNotificationsReadMutation,
} from '../slices/adminNotificationApiSlice';

const DEFAULT_QUERY_PARAMS = {
    onlyUnread: true,
    pageSize: 10,
    pageNumber: 1,
};

const getErrorStatus = (error) => error?.status || error?.originalStatus || error?.data?.statusCode;

const useAdminNotifications = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const isAdmin = Boolean(userInfo?.isAdmin);

    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const shouldSkip = !isAdmin || isUnauthorized;

    const {
        data,
        error,
        isFetching,
        refetch,
    } = useGetAdminNotificationsQuery(DEFAULT_QUERY_PARAMS, {
        skip: shouldSkip,
        pollingInterval: 45000,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

    const [markAsRead, { isLoading: isMarkingAsRead }] = useMarkAdminNotificationsReadMutation();

    useEffect(() => {
        if (!isAdmin) {
            setIsUnauthorized(false);
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [isAdmin]);

    useEffect(() => {
        const status = getErrorStatus(error);
        if (status === 401 || status === 403) {
            setIsUnauthorized(true);
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [error]);

    useEffect(() => {
        if (!data) {
            return;
        }

        const nextNotifications = Array.isArray(data.notifications) ? data.notifications : [];
        const nextUnreadCount = typeof data.unreadCount === 'number'
            ? data.unreadCount
            : nextNotifications.filter((notification) => !notification.isRead).length;

        setNotifications(nextNotifications);
        setUnreadCount(Math.max(nextUnreadCount, 0));
    }, [data]);

    const markNotificationAsRead = async (notificationId) => {
        if (!notificationId) {
            return;
        }

        const notification = notifications.find((item) => (item._id || item.id) === notificationId);
        if (!notification || notification.isRead) {
            return;
        }

        const previousNotifications = notifications;
        const previousUnreadCount = unreadCount;

        setNotifications((currentNotifications) => currentNotifications.map((item) => {
            const currentId = item._id || item.id;
            if (currentId !== notificationId) {
                return item;
            }

            return { ...item, isRead: true };
        }));
        setUnreadCount((currentUnreadCount) => Math.max(currentUnreadCount - 1, 0));

        try {
            await markAsRead({ notificationIds: [notificationId], markAll: false }).unwrap();
        } catch (markReadError) {
            const status = getErrorStatus(markReadError);
            if (status === 401 || status === 403) {
                setIsUnauthorized(true);
                setNotifications([]);
                setUnreadCount(0);
                return;
            }

            setNotifications(previousNotifications);
            setUnreadCount(previousUnreadCount);
        }
    };

    const markAllAsRead = async () => {
        if (notifications.length === 0 || unreadCount === 0) {
            return;
        }

        const previousNotifications = notifications;
        const previousUnreadCount = unreadCount;

        setNotifications((currentNotifications) => currentNotifications.map((item) => ({
            ...item,
            isRead: true,
        })));
        setUnreadCount(0);

        try {
            await markAsRead({ markAll: true }).unwrap();
        } catch (markReadError) {
            const status = getErrorStatus(markReadError);
            if (status === 401 || status === 403) {
                setIsUnauthorized(true);
                setNotifications([]);
                setUnreadCount(0);
                return;
            }

            setNotifications(previousNotifications);
            setUnreadCount(previousUnreadCount);
        }
    };

    const handleDropdownToggle = (isOpen) => {
        if (isOpen && !shouldSkip) {
            refetch();
        }
    };

    return {
        notifications,
        unreadCount,
        isLoading: isFetching,
        isMarkingAsRead,
        hideBell: shouldSkip,
        markNotificationAsRead,
        markAllAsRead,
        handleDropdownToggle,
    };
};

export default useAdminNotifications;


